import { useEffect, useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import type {
  CreditCard,
  FinanceSummary,
  PaymentRecord,
  Subscription,
  CalendarEvent,
  ProjectionMonth,
} from "../../lib/types";
import { SummaryCards } from "./SummaryCards";
import { CardsTable } from "./CardsTable";
import { PaymentModal } from "./PaymentModal";
import { PaymentTimeline } from "./PaymentTimeline";
import { SubscriptionsTable } from "./SubscriptionsTable";
import { PaymentHistory } from "./PaymentHistory";
import { LiquidationProgress } from "./LiquidationProgress";

export function FinanzasPage() {
  const [summary, setSummary] = useState<FinanceSummary>({
    total_debt: 0,
    total_available: 0,
    monthly_interest: 0,
    monthly_subscriptions: 0,
  });
  const [cards, setCards] = useState<CreditCard[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [calendar, setCalendar] = useState<CalendarEvent[]>([]);
  const [snapshots, setSnapshots] = useState<ProjectionMonth[]>([]);
  const [payingCard, setPayingCard] = useState<CreditCard | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, cardsRes, payRes, subsRes, calRes, projRes] =
        await Promise.all([
          api.finances.summary(),
          api.finances.cards(),
          api.finances.payments(20),
          api.finances.subscriptions(),
          api.finances.calendar(),
          api.finances.projection(24, 8000),
        ]);
      setSummary(sumRes);
      setCards(cardsRes.cards);
      setPayments(payRes.payments);
      setSubscriptions(subsRes.subscriptions);
      setCalendar(calRes.events);
      setSnapshots(projRes.snapshots);
    } catch (err) {
      console.error("[Finanzas] Load error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [load]);

  const handleUpdateBalance = async (id: string, balance: number) => {
    try {
      await api.finances.updateCard(id, { balance });
      await load();
    } catch (err) {
      console.error("[Finanzas] Update error:", err);
    }
  };

  const handlePay = async (cardId: string, amount: number, note: string) => {
    try {
      await api.finances.pay(cardId, amount, "manual", note || undefined);
      await load();
    } catch (err) {
      console.error("[Finanzas] Pay error:", err);
    }
  };

  const handleToggleSubscription = async (id: number, active: boolean) => {
    try {
      await api.finances.updateSubscription(id, { active: active ? 1 : 0 });
      await load();
    } catch (err) {
      console.error("[Finanzas] Toggle sub error:", err);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-100">
            Sistema Financiero
          </h2>
          <p className="text-sm text-gray-500">
            Deuda, tarjetas, suscripciones y proyeccion de liquidacion
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="btn-ghost"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {/* Section 1: Summary Cards */}
      <SummaryCards summary={summary} />

      {/* Section 2: Cards Table */}
      <CardsTable
        cards={cards}
        onUpdateBalance={handleUpdateBalance}
        onPayClick={setPayingCard}
      />

      {/* Section 3 + 4: Timeline + Subscriptions side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PaymentTimeline events={calendar} />
        <SubscriptionsTable
          subscriptions={subscriptions}
          onToggle={handleToggleSubscription}
        />
      </div>

      {/* Section 5 + 6: History + Liquidation side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PaymentHistory payments={payments} />
        <LiquidationProgress summary={summary} snapshots={snapshots} />
      </div>

      {/* Payment Modal */}
      {payingCard && (
        <PaymentModal
          card={payingCard}
          onClose={() => setPayingCard(null)}
          onPay={handlePay}
        />
      )}
    </div>
  );
}
